import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/Button';
import { FileStack, ArrowRight as ArrowRightIcon, Globe, BarChart2, RefreshCw, Smartphone, Route, Network, Brain, RotateCw, XIcon, CheckIcon, CheckCircle2 } from 'lucide-react';
interface SlideFrictionToFlowProps {
  onContinue: () => void;
}
interface FlowItem {
  todayIcon: React.ReactNode;
  todayLabel: string;
  todayMicro: string;
  snakkerIcon: React.ReactNode;
  snakkerLabel: string;
  snakkerMicro: string;
}
export function SlideFrictionToFlow({
  onContinue
}: SlideFrictionToFlowProps) {
  const [isFuture, setIsFuture] = useState(false);
  const items: FlowItem[] = [{
    todayIcon: <FileStack className="w-5 h-5" data-id="element-165" />,
    todayLabel: 'Static documents',
    todayMicro: 'Built to show — not engage',
    snakkerIcon: <Smartphone className="w-5 h-5" data-id="element-166" />,
    snakkerLabel: 'Interactive Snaks',
    snakkerMicro: 'Built to engage and learn'
  }, {
    todayIcon: <ArrowRightIcon className="w-5 h-5" data-id="element-167" />,
    todayLabel: 'Single push',
    todayMicro: 'One message, no progression',
    snakkerIcon: <Route className="w-5 h-5" data-id="element-168" />,
    snakkerLabel: 'Multi-step journeys',
    snakkerMicro: 'Adaptive, sequential flow'
  }, {
    todayIcon: <Globe className="w-5 h-5" data-id="element-169" />,
    todayLabel: 'Rework per market',
    todayMicro: 'Slow and expensive',
    snakkerIcon: <Network className="w-5 h-5" data-id="element-170" />,
    snakkerLabel: 'Master → Local',
    snakkerMicro: 'One source, many markets'
  }, {
    todayIcon: <BarChart2 className="w-5 h-5" data-id="element-171" />,
    todayLabel: 'Opens & clicks',
    todayMicro: 'No behavioral depth',
    snakkerIcon: <Brain className="w-5 h-5" data-id="element-172" />,
    snakkerLabel: 'Behavioral insight',
    snakkerMicro: 'What drives decisions'
  }, {
    todayIcon: <RefreshCw className="w-5 h-5" data-id="element-173" />,
    todayLabel: 'Late reports',
    todayMicro: 'Never applied',
    snakkerIcon: <RotateCw className="w-5 h-5" data-id="element-174" />,
    snakkerLabel: 'Continuous loop',
    snakkerMicro: 'Always improving'
  }];
  return <div className="h-full w-full flex flex-col px-8 py-10 md:px-14 md:py-12 lg:px-20 lg:py-14 max-w-7xl mx-auto" data-id="element-175">
      <motion.h2 initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="text-3xl md:text-4xl lg:text-5xl font-serif leading-tight text-white mb-8 text-center" data-id="element-176">
        From friction <span className="text-teal-400" data-id="element-177">→</span> to flow
      </motion.h2>

      <div className="flex-1 flex flex-col items-center" data-id="element-178">
        {/* Toggle */}
        <div className="flex bg-navy-900 p-1.5 rounded-full mb-8 border border-navy-600 relative" data-id="element-179">
          <motion.div className="absolute top-1.5 bottom-1.5 w-[calc(50%-6px)] bg-teal-400 rounded-full shadow-lg" animate={{
          left: isFuture ? 'calc(50% + 3px)' : '6px'
        }} transition={{
          type: 'spring',
          stiffness: 300,
          damping: 30
        }} data-id="element-180" />
          <button onClick={() => setIsFuture(false)} className={`relative z-10 px-8 py-2.5 rounded-full font-medium transition-colors ${!isFuture ? 'text-navy-900' : 'text-white/80 hover:text-white'}`} data-id="element-181">
            Today
          </button>
          <button onClick={() => setIsFuture(true)} className={`relative z-10 px-8 py-2.5 rounded-full font-medium transition-colors ${isFuture ? 'text-navy-900' : 'text-white/80 hover:text-white'}`} data-id="element-182">
            Snakker
          </button>
        </div>

        {/* Cards */}
        <div className="w-full max-w-5xl flex-1 flex flex-col justify-center" data-id="element-183">
          <AnimatePresence mode="wait" data-id="element-184">
            <motion.div key={isFuture ? 'future' : 'today'} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} transition={{
            duration: 0.3
          }} className="space-y-3" data-id="element-185">
              {items.map((item, idx) => <motion.div key={idx} initial={{
              opacity: 0,
              x: isFuture ? 10 : -10
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: idx * 0.06
            }} className={`flex items-center gap-4 rounded-xl px-5 py-3.5 border ${isFuture ? 'bg-teal-400/5 border-teal-400/20' : 'bg-navy-700/40 border-navy-600'}`} data-id="element-186">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${isFuture ? 'bg-teal-400/15 text-teal-400' : 'bg-navy-600 text-white/40'}`} data-id="element-187">
                    {isFuture ? item.snakkerIcon : item.todayIcon}
                  </div>

                  <div className="flex-1 min-w-0" data-id="element-188">
                    <div className="flex items-center gap-2" data-id="element-189">
                      <span className={`font-semibold text-sm ${isFuture ? 'text-white' : 'text-white/70'}`} data-id="element-190">
                        {isFuture ? item.snakkerLabel : item.todayLabel}
                      </span>
                    </div>
                    <span className={`text-xs ${isFuture ? 'text-white/60' : 'text-white/40'}`} data-id="element-191">
                      {isFuture ? item.snakkerMicro : item.todayMicro}
                    </span>
                  </div>

                  <div className="flex-shrink-0" data-id="element-192">
                    {isFuture ? <CheckIcon className="w-4 h-4 text-teal-400" data-id="element-193" /> : <XIcon className="w-4 h-4 text-red-400/50" data-id="element-194" />}
                  </div>
                </motion.div>)}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Bottom line + CTA */}
        <div className="mt-6 flex flex-col items-center gap-4" data-id="element-195">
          <motion.p initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.5
        }} className="text-center" data-id="element-196">
            <span className="text-white/50" data-id="element-197">Not new work</span>
            <span className="text-teal-400 mx-2" data-id="element-198">→</span>
            <span className="text-teal-400 font-medium" data-id="element-199">
              Better connected work
            </span>
          </motion.p>

          <motion.div animate={{
          opacity: isFuture ? 1 : 0,
          y: isFuture ? 0 : 10
        }} data-id="element-200">
            <Button onClick={onContinue} className={isFuture ? 'pointer-events-auto' : 'pointer-events-none'} data-id="element-201">
              See how it runs
            </Button>
          </motion.div>
        </div>
      </div>
    </div>;
}